/* ASH purchase clarity: display enhancement around Dawn's existing commerce controls.
   Never changes variant IDs, quantity rules, form submission, or payment widgets. */
if (!customElements.get('ash-pack-clarity')) {
  customElements.define('ash-pack-clarity', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('[id^="MainProduct-"]');
      this.config = JSON.parse(this.querySelector('[data-ash-config]').textContent);
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!this.form || !this.button) return;
      this.quantity = this.root.querySelector('input[name="quantity"]');
      this.sticky = this.root.querySelector('[data-ash-sticky]');
      this.mobileButton = this.root.querySelector('[data-ash-mobile-add]');
      this.format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style:'currency', currency:this.config.currency
      });
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.root.addEventListener('change', () => queueMicrotask(() => this.update()), options);
      this.root.addEventListener('input', e => {
        if (e.target === this.quantity) this.update();
      }, options);
      this.mobileButton?.addEventListener('click', () => {
        this.update();
        if (this.mobileButton.disabled) return;
        if (this.quantity && !this.quantity.checkValidity()) {
          this.quantity.reportValidity();
          this.quantity.scrollIntoView({block:'center'});
          return;
        }
        // Native button drives the original product-form handler and cart events.
        this.button.click();
      }, options);
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.button, {attributes:true,attributeFilter:['disabled','aria-disabled','class']});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event.data?.sectionId === this.sectionId) this.update();
        });
        this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.update());
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, event => {
          if (String(event.productVariantId) === String(this.form.querySelector('[name=id]').value)) {
            this.root.querySelector('.product-form__error-message-wrapper')?.scrollIntoView({block:'center'});
          }
        });
      }
      this.attachWeights();
      this.update();
    }
    currentVariant() {
      const picker = this.root.querySelector('variant-radios, variant-selects');
      if (picker) {
        const fields = [...picker.querySelectorAll('fieldset')];
        const choices = fields.length ? fields.map(f=>f.querySelector('input:checked')?.value)
          : [...picker.querySelectorAll('select')].map(s=>s.value);
        if (choices.length) return this.config.variants.find(v=>v.options.every((x,i)=>x===choices[i]));
      }
      return this.config.variants.find(v=>String(v.id)===String(this.form.querySelector('[name=id]')?.value));
    }
    attachWeights() {
      const index = this.config.optionIndex;
      if (index === null) return;
      const field = this.root.querySelectorAll('variant-radios fieldset')[index];
      if (!field) return;
      field.querySelectorAll('input[type=radio]').forEach(input=>{
        const variant = this.config.variants.find(v=>v.length===input.value);
        if (!variant?.grams) return;
        const label = [...field.querySelectorAll('label')].find(l=>l.htmlFor===input.id);
        if (!label || label.querySelector('.ash-option-weight')) return;
        const caption = document.createElement('small');
        caption.className = 'ash-option-weight';
        caption.textContent = `${variant.grams} g / ${this.config.strings.pack}`;
        label.append(caption);
      });
    }
    text(selector, value) {
      this.root.querySelectorAll(selector).forEach(node=>{
        if (node.textContent !== value) node.textContent = value;
      });
    }
    update() {
      const v = this.currentVariant(), c = this.config, s = c.strings;
      const q = Number(this.quantity?.value || 1);
      const validQ = Number.isInteger(q) && q > 0 && (!this.quantity || this.quantity.checkValidity());
      const blocked = !v?.available || !validQ || this.button.disabled ||
        this.button.getAttribute('aria-disabled') === 'true' || this.button.classList.contains('loading');
      const count = `${q} ${q===1?s.pack:s.packs}`;
      const selection = v && validQ ? `${v.title} · ${count} · ${q*c.wefts} ${s.wefts}` : '';
      const total = v && validQ ? this.format.format(v.price*q/100) : '';
      this.text('[data-ash-selection]', selection);
      this.text('[data-ash-quantity]',v && validQ ? `${count} = ${q*c.wefts} ${s.wefts}${v.grams ? ` · ${q*v.grams} ${s.grams} ${s.total}` : ''}` : '');
      this.text('[data-ash-length]', v?.length || '');
      this.text('[data-ash-length-help]', v?.help || '');
      this.text('[data-ash-weight]', v?.grams ? `${v.grams} g` : '');
      this.root.querySelector('[data-ash-weight-wrap]').hidden = !v?.grams;
      const priceNode = this.root.querySelector(`#price-${this.sectionId} .price`);
      if (priceNode && !priceNode.querySelector('.ash-price-unit')) {
        const unit=document.createElement('small');unit.className='ash-price-unit';unit.textContent=c.packUnit;priceNode.append(unit);
      }
      let price = this.button.querySelector('.ash-button-total');
      if (!price) {price=document.createElement('span');price.className='ash-button-total';this.button.append(price);}
      price.textContent = v?.available ? total : '';
      if (this.mobileButton) {
        this.mobileButton.disabled = blocked;
        this.mobileButton.textContent = !v ? s.unavailable : !v.available ? s.soldOut : s.add;
        this.mobileButton.setAttribute('aria-busy',String(this.button.classList.contains('loading')));
        this.text('[data-ash-mobile-label]', v && validQ ? `${v.title} · ${count}` : '');
        this.text('[data-ash-mobile-total]', total);
        this.sticky.hidden = false;
        document.body.classList.add('ash-mobile-active');
      }
    }
    disconnectedCallback() {
      this.abort?.abort();this.observer?.disconnect();this.unsubscribe?.();this.unsubscribeQuantity?.();this.unsubscribeError?.();
      document.body.classList.remove('ash-mobile-active');this.initialized=false;
    }
  });
}
